import { Injectable } from '@angular/core';
import { AuthService } from './auth.service';
import { ConfigService } from './config.service';
import { ModelService } from './model.service';
import { FullPublishResult, ModelData, PublishResult, QuerySummaryDto, TargetsModel, TargetWellboreResolution, UserContext } from '../models/interfaces';
import { environment } from '../../environments/environment';

@Injectable({ providedIn: 'root' })
export class ConsumptionHubService {

  constructor(
    private auth: AuthService,
    private config: ConfigService,
    private model: ModelService
  ) {}

  async fetchUserContext(): Promise<UserContext> {
    const accessToken = this.requireAccessToken();

    // Decode and log JWT claims to help diagnose audience mismatches
    try {
      const [, payload] = accessToken.split('.');
      const pad = '='.repeat((4 - (payload.length % 4)) % 4);
      const claims = JSON.parse(atob((payload + pad).replace(/-/g, '+').replace(/_/g, '/')));
      console.log('[Auth] JWT claims:', { aud: claims.aud, iss: claims.iss, scp: claims.scp, scope: claims.scope });
    } catch { /* ignore */ }

    // Use proxy path in local dev to avoid CORS; use real API URL on Azure.
    const apiBase = environment.apiBaseUrlOverride || 'https://api.delfi.slb.com';
    const contextUrl = `${apiBase}/ccm/userContext/v1/currentContext`;

    const response = await fetch(contextUrl, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        appkey: this.config.appKey
      }
    });

    if (!response.ok) {
      const text = await response.text();
      throw new Error(`Context fetch failed: ${response.status} ${response.statusText} - ${text}`);
    }

    const context: UserContext = await response.json();

    const partitionId = context?.dataPartition?.slbPartitionId;
    if (partitionId) {
      this.config.setDataPartition(partitionId);
    }

    const instance = context?.dataPartition?.instance;
    if (instance) {
      this.config.setConsumptionHubInstance(instance);
    }

    return context;
  }

  async publishWell(): Promise<PublishResult> {
    const { wells } = this.model.getPayloads();
    return this.request('POST', this.buildDataSourceUrl('wells'), wells);
  }

  async publishWellbore(): Promise<PublishResult> {
    const { wellbores } = this.model.getPayloads();
    return this.request('POST', this.buildDataSourceUrl('wellbores'), wellbores);
  }

  async publishTrajectory(): Promise<PublishResult> {
    const { trajectories } = this.model.getPayloads();
    return this.request('POST', this.buildDataSourceUrl('trajectories'), trajectories);
  }

  async publishFullModel(): Promise<FullPublishResult> {
    if (!this.model.hasModel()) {
      throw new Error('No model loaded. Please import a file before publishing.');
    }
    const toResult = async (fn: () => Promise<PublishResult>): Promise<PublishResult> => {
      try { return await fn(); }
      catch (e: any) { return { status: 0, body: e.message }; }
    };
    const well = await toResult(() => this.publishWell());
    const wellbore = await toResult(() => this.publishWellbore());
    const trajectory = await toResult(() => this.publishTrajectory());
    const failed = [well, wellbore, trajectory].filter(r => !r.status || r.status >= 400);
    if (failed.length > 0) {
      throw new Error(
        [well, wellbore, trajectory]
          .map((r, i) => {
            const label = ['Well', 'Wellbore', 'Trajectory'][i];
            return r.status && r.status < 400
              ? `${label}: ${r.status} OK`
              : `${label}: FAILED - ${typeof r.body === 'string' ? r.body : JSON.stringify(r.body)}`;
          })
          .join(' | ')
      );
    }
    return { well, wellbore, trajectory };
  }

  async deleteDataSource(): Promise<PublishResult> {
    return this.request('DELETE', this.buildDataSourceUrl());
  }

  importModel(modelData: ModelData): ModelData {
    return this.model.replaceModel(modelData);
  }

  // --- Targets (separate payload, with imported or pre-existing parents) ---

  /** Store a parsed targets model (from a Targets workbook/CSV). */
  importTargets(targetsModel: TargetsModel): TargetsModel {
    return this.model.replaceTargets(targetsModel);
  }

  /** Query the wellbores already published for this data source. */
  async fetchPublishedWellbores(): Promise<QuerySummaryDto[]> {
    const result = await this.request('GET', this.buildDataSourceUrl('wellbores/summary'));
    return this.requireSummaryArray(result.body, 'wellbores');
  }

  /** Query the wells already published for this data source. */
  async fetchPublishedWells(): Promise<QuerySummaryDto[]> {
    const result = await this.request('GET', this.buildDataSourceUrl('wells/summary'));
    return this.requireSummaryArray(result.body, 'wells');
  }

  /** Query the wellbores published under a specific well. */
  async fetchWellboresForWell(wellId: string): Promise<QuerySummaryDto[]> {
    const path = `wellbores/summary?well_id=${encodeURIComponent(wellId)}`;
    const result = await this.request('GET', this.buildDataSourceUrl(path));
    return this.requireSummaryArray(result.body, 'wellbores');
  }

  /**
   * Fetch the wellbores for each published well that the loaded targets resolve
   * to by name, keyed by well id. Only matched wells are queried.
   */
  private async buildWellboreIndex(
    publishedWells: QuerySummaryDto[]
  ): Promise<Map<string, QuerySummaryDto[]>> {
    const wellboresByWellId = new Map<string, QuerySummaryDto[]>();
    for (const wellId of this.model.matchedWellIds(publishedWells)) {
      wellboresByWellId.set(wellId, await this.fetchWellboresForWell(wellId));
    }
    return wellboresByWellId;
  }

  /** Query the target sets already published for this data source. */
  async fetchPublishedTargets(): Promise<QuerySummaryDto[]> {
    const result = await this.request('GET', this.buildDataSourceUrl('targets/summary'));
    return this.requireSummaryArray(result.body, 'targets');
  }

  /**
   * Resolve each imported target set's WELL NAME to a published wellbore id.
   * The caller surfaces `unresolved` as a blocking warning before publishing.
   */
  async resolveTargetWellbores(): Promise<TargetWellboreResolution> {
    if (this.model.hasTargetParentData()) {
      return this.model.resolveTargetWellbores([], new Map());
    }
    const wells = await this.fetchPublishedWells();
    const wellboresByWellId = await this.buildWellboreIndex(wells);
    return this.model.resolveTargetWellbores(wells, wellboresByWellId);
  }

  /**
   * Publish the imported targets. WELL NAMEs are resolved to the actual
    * published wellbore ids for legacy templates. Self-contained templates
    * publish their explicit well and wellbore records first.
   *
   * A 403 (partition not provisioned / no write access) and a 400 (payload
   * validation) are translated into actionable, user-facing messages.
   */
  async publishTargets(): Promise<PublishResult> {
    if (!this.model.hasTargets()) {
      throw new Error('No targets loaded. Please import a targets file before publishing.');
    }
    let wells: QuerySummaryDto[] = [];
    let wellboresByWellId = new Map<string, QuerySummaryDto[]>();
    if (this.model.hasTargetParentData()) {
      const parents = this.model.getTargetParentPayloads();
      await this.request('POST', this.buildDataSourceUrl('wells'), parents.wells);
      await this.request('POST', this.buildDataSourceUrl('wellbores'), parents.wellbores);
    } else {
      wells = await this.fetchPublishedWells();
      wellboresByWellId = await this.buildWellboreIndex(wells);
    }
    const payload = this.model.getTargetsPayload(wells, wellboresByWellId);
    try {
      return await this.request('POST', this.buildDataSourceUrl('targets'), payload);
    } catch (error) {
      throw this.describeTargetsPublishError(error);
    }
  }

  /**
   * Delete every target set published for this data source. Target ids are
   * queried from the summary endpoint and removed via batch-delete; if there
   * are none, no API call is made.
   */
  async deleteTargets(): Promise<PublishResult> {
    const published = await this.fetchPublishedTargets();
    const ids = published
      .map(target => (target?.id ?? '').trim())
      .filter(id => id.length > 0);

    if (ids.length === 0) {
      return { status: 204, body: 'No targets found to delete for this data source.' };
    }

    const result = await this.request('POST', this.buildDataSourceUrl('targets/batch-delete'), ids);
    return { status: result.status, body: `Deleted ${ids.length} target set(s).` };
  }

  // --- Private helpers ---

  /** Turn a raw targets-publish failure into an actionable message. */
  private describeTargetsPublishError(error: unknown): Error {
    const status = (error as { status?: number })?.status;
    const body = (error as { body?: unknown })?.body;

    if (status === 403) {
      return new Error(
        `Publishing targets was forbidden (403). The data partition ` +
        `'${this.config.slbDataPartitionID}' is not provisioned for the Consumption Hub, ` +
        `or your account lacks write access to data source '${this.config.dataSource}'. ` +
        `Confirm the partition is enabled for DrillPlan and that you have ingress permissions, then retry.`
      );
    }

    if (status === 400) {
      const detail = this.extractValidationDetail(body);
      return new Error(
        `The targets payload was rejected as invalid (400).` +
        (detail ? ` ${detail}` : '') +
        ` Verify the target coordinates, coordinate_input_type, and wellbore mapping, ` +
        `then re-import the targets file and try again.`
      );
    }

    return error instanceof Error ? error : new Error(String(error));
  }

  /** Pull a human-readable summary out of an RFC7807 ValidationProblemDetails body. */
  private extractValidationDetail(body: unknown): string {
    if (!body || typeof body !== 'object') {
      return typeof body === 'string' ? body : '';
    }
    const problem = body as { title?: string; detail?: string; errors?: Record<string, unknown> };
    const parts: string[] = [];
    if (problem.title) parts.push(problem.title);
    if (problem.detail) parts.push(problem.detail);
    if (problem.errors && typeof problem.errors === 'object') {
      for (const [field, messages] of Object.entries(problem.errors)) {
        const list = Array.isArray(messages) ? messages.join('; ') : String(messages);
        parts.push(`${field}: ${list}`);
      }
    }
    return parts.join(' — ');
  }

  private buildDataSourceUrl(path = ''): string {
    const suffix = path ? `/${path}` : '';
    return `${this.config.consumptionHubBaseUrl}/data-sources/${this.config.dataSource}${suffix}`;
  }

  private requireSummaryArray(body: unknown, resource: string): QuerySummaryDto[] {
    if (Array.isArray(body)) return body as QuerySummaryDto[];
    throw new Error(
      `The ${resource} summary response was not a valid JSON array. ` +
      `Check the Azure proxy response encoding and try again.`
    );
  }

  private requireAccessToken(): string {
    const token = this.auth.getAccessToken();
    if (!token) throw new Error('No access token. Please sign in first.');
    return token;
  }

  private async request(method: string, url: string, payload?: unknown): Promise<PublishResult> {
    const accessToken = this.requireAccessToken();

    if (!this.config.slbDataPartitionID) {
      throw new Error('No partition selected yet. Fetch context first.');
    }

    const response = await fetch(url, {
      method,
      headers: {
        Accept: '*/*',
        'Content-Type': 'application/json',
        'slb-partition-id': this.config.slbDataPartitionID,
        appKey: this.config.appKey,
        Authorization: `Bearer ${accessToken}`
      },
      body: payload ? JSON.stringify(payload) : undefined
    });

    const body = await this.parseResponseBody(response);

    if (!response.ok) {
      const text = typeof body === 'string' ? body : JSON.stringify(body);
      const error = new Error(`${method} failed: ${response.status} ${response.statusText} - ${text}`) as Error & {
        status?: number;
        body?: unknown;
      };
      error.status = response.status;
      error.body = body;
      throw error;
    }

    return { status: response.status, body };
  }

  private async parseResponseBody(response: Response): Promise<unknown> {
    const text = await response.text();
    if (!text) return null;
    try {
      return JSON.parse(text);
    } catch {
      return text;
    }
  }
}
